import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { v4 as uuid } from 'uuid';
import PropTypes from 'prop-types';
import { menuFor, sharedMenuFor } from '../../services/smorg_board';
import {
  getEditEventsForUndoIngredientEditOperation,
  getSuggestionsFromHistoryOperation,
  getSuggestionsOperation,
  scaleMeal,
  storeMealEditEventOperation,
  updateFoodBrainDerivedDataOnMeal,
} from '../../operations/meal_operations';
import { addedItems, deduplicate } from '../../services/arrays';
import { uploadUserImage } from '../../services/user_generated_content';
import MealDetailModal from './meal_detail_modal';
import {
  useGetFullMealQuery,
  useGetFullSharedMealQuery,
  useGetSharedBoardQuery,
  useGetSmorgBoardQuery,
  useGetSpaceQuery,
  useLazyGetFullMealQuery,
  useUpdateMealMutation,
  useUpdateUserProfileMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import {
  customerFoodDataIDOfSpace,
  DEFAULT_SPACE_ID,
} from '../../services/spaces';
import {
  healthProGroupForSpaceID,
  userIsCreator,
  userLocale,
} from '../../services/users';
import { SMORG_NUTRITION_ADDON } from '../../services/addons';
import { useTracker } from '../../operations/tracking_operations';
import { collectIngredientEditEventOperation } from '../../operations/collect_operations';
import {
  mealWithAddonEnabled,
  mealWithArraySectionDeleted,
  mealWithArraySectionUpdated,
  mealWithIngredientCheckStateChanged,
  mealWithIngredientScalingRuleUpdated,
  mealWithIngredientTokensReset,
  mealWithNewArraySectionItem,
  mealWithSingleSectionUpdated,
  mealWithStructuredIngredientUpdated,
} from '../../services/meals';
import {
  useGetCurrentSpaceMembershipQuery,
  useGetEndUserSpaceQuery,
  useGetSharedProgrammeRecipesBoardIDsForEndUserSearchQuery,
  useGetUserProfileQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { SHARED_BOARDS_REFRESH_INTERVAL_SECONDS } from '../../services/end_user_published_resources';

const MealDetailContainer = ({
  parentSmorgBoardId,
  mealId,
  menuId,
  visible,
  // isPreview,
  dismiss,
}) => {
  const dispatch = useDispatch();

  // Must not access state from within callbacks passed to ContentEditable.
  // The issue is described at:
  // https://github.com/lovasoa/react-contenteditable/issues/161
  // We no longer use ContentEditable so this can be removed.
  const currentTextRef = useRef();
  const currentRecipeRef = useRef();

  const { data: userProfile } = useGetUserProfileQuery();

  const locale = userLocale(userProfile);

  const isSmorgStudio = userIsCreator(userProfile);

  const { data: currentSpaceMembership } = useGetCurrentSpaceMembershipQuery(
    undefined,
    {
      skip: !userProfile || isSmorgStudio,
    },
  );

  const [currentlyEditingInline, setCurrentlyEditingInline] = useState({
    section: null,
    itemId: null,
  });

  const { data: smorgBoard, isLoading: isSmorgBoardLoading } =
    useGetSmorgBoardQuery(parentSmorgBoardId, {
      skip: !parentSmorgBoardId || !mealId,
    });

  const {
    data: sharedBoard,
    error: sharedBoardError,
    isLoading: isSharedBoardLoading,
  } = useGetSharedBoardQuery(parentSmorgBoardId, {
    skip: !parentSmorgBoardId || !mealId,
    refetchOnMountOrArgChange: SHARED_BOARDS_REFRESH_INTERVAL_SECONDS,
  });

  const tracker = useTracker();

  const { data: currentCreatorSpace } = useGetSpaceQuery(undefined, {
    skip: !userProfile || !isSmorgStudio,
  });

  const { data: currentEndUserSpace } = useGetEndUserSpaceQuery(undefined, {
    skip: !userProfile || isSmorgStudio,
  });

  const currentSpaceID = isSmorgStudio
    ? currentCreatorSpace?.id
    : currentSpaceMembership?.spaceID;

  const customerFoodDataID = customerFoodDataIDOfSpace(currentSpaceID);

  const currentHealthProGroup =
    currentCreatorSpace && healthProGroupForSpaceID(currentCreatorSpace.id);

  const [lazyGetMeal] = useLazyGetFullMealQuery();

  const [updateMeal] = useUpdateMealMutation();

  const [updateUserProfile] = useUpdateUserProfileMutation();

  const isSharedMeal =
    !sharedBoardError && !isSharedBoardLoading && !!sharedBoard;

  const { data: fullSmorgBoardMeal, isLoading: isFullSmorgBoardMealLoading } =
    useGetFullMealQuery(mealId, {
      skip: isSharedMeal,
    });

  const { data: fullSharedMeal, isLoading: isFullSharedMealLoading } =
    useGetFullSharedMealQuery(mealId, {
      skip: !isSharedMeal,
    });

  const fullMeal = isSharedMeal ? fullSharedMeal : fullSmorgBoardMeal;
  const isFullMealLoading = isSharedMeal
    ? isFullSharedMealLoading
    : isFullSmorgBoardMealLoading;

  const { data: programmeRecipesBoardsIDs } =
    useGetSharedProgrammeRecipesBoardIDsForEndUserSearchQuery();

  const spaceAssignedSharedBoardIDs =
    currentEndUserSpace?.sharedRecipesBoardIDs || [];

  const parentIDsOfReadOnlyMeals = [
    ...(programmeRecipesBoardsIDs || []),
    ...spaceAssignedSharedBoardIDs,
  ];

  const isReadOnly = parentIDsOfReadOnlyMeals.includes(parentSmorgBoardId);

  const isCommunityUser = currentSpaceMembership?.spaceID === DEFAULT_SPACE_ID;

  const menu = useMemo(() => {
    if (smorgBoard) {
      return menuFor(smorgBoard, mealId, menuId);
    }
    if (sharedBoard) {
      sharedMenuFor(sharedBoard, mealId, menuId);
    }
    return null;
  }, [mealId, menuId, sharedBoard, smorgBoard]);

  const recipe = fullMeal && fullMeal.recipes[0];

  const [scaleToServings, setScaleToServings] = useState(recipe?.servings);
  const [savingInProgress, setSavingInProgress] = useState(false);

  const dismissEditingInline = () => {
    currentTextRef.current = null;
    setCurrentlyEditingInline({ section: null, itemId: null });
  };

  useEffect(() => {
    currentRecipeRef.current = recipe;
    setScaleToServings(recipe?.servings);
    setSavingInProgress(false);
  }, [recipe]);

  // useEffect(() => {
  //   if (!mealId) {
  //     return;
  //   }
  //   dismissEditingInline();
  //   updateFoodBrainDerivedDataOnMeal(meal, customerFoodDataID, updateMeal);
  // }, [customerFoodDataID, meal, mealId, updateMeal]);

  const [
    ingredientSuggestionsNetworkState,
    setIngredientSuggestionsNetworkState,
  ] = useState({ loading: false, loaded: false, error: null });

  const [ingredientSuggestions, setIngredientSuggestions] = useState([]);

  const handleArraySectionItemsPasted = async (section, itemId, pastedRows) => {
    const pasteIndex = recipe[section].findIndex((item) => item.id === itemId);
    if (pasteIndex === -1) {
      console.warn(`Could not find ${section} ${itemId}, should never happen!`);
      return;
    }
    let updatedMeal = fullMeal;
    if (section === 'ingredients') {
      updatedMeal = mealWithIngredientTokensReset(fullMeal, itemId);
    }
    updatedMeal = mealWithArraySectionUpdated(
      updatedMeal,
      section,
      itemId,
      pastedRows[0],
    );
    for (let i = 1; i < pastedRows.length; i += 1) {
      const newId = uuid();
      updatedMeal = mealWithNewArraySectionItem(
        updatedMeal,
        section,
        pasteIndex + i,
        newId,
        pastedRows[i],
      );
    }
    updateMeal(updatedMeal);
    if (section === 'ingredients') {
      await updateFoodBrainDerivedDataOnMeal(
        updatedMeal.id,
        updatedMeal.recipes[0],
        updatedMeal.derivedNutrition?.ingredientNutrition,
        customerFoodDataID,
        lazyGetMeal,
        updateMeal,
      );
      // collectIngredientEditEventOperation(
      //   mealId,
      //   recipe.mealiqId || '',
      //   sectionItem.id,
      //   originalText,
      //   text,
      //   editMethod,
      //   currentHealthProGroup,
      // );
      // await storeMealEditEventOperation(
      //   mealId,
      //   section,
      //   itemId,
      //   originalText,
      //   currentHealthProGroup,
      // );
    }
    tracker(userProfile, ['Edit meal'], ['numMealsEdited']);
  };

  const handleArraySectionItemUpdated = async (
    section,
    itemId,
    text,
    appendedNewItemId,
    editMethod,
  ) => {
    console.log('handleArraySectionItemUpdated');
    const sectionItem = recipe[section].find((item) => item.id === itemId);
    const originalText = sectionItem.fullText;
    if (originalText !== text || appendedNewItemId) {
      let updatedMeal = fullMeal;
      if (section === 'ingredients') {
        updatedMeal = mealWithIngredientTokensReset(fullMeal, itemId);
      }
      updatedMeal = mealWithArraySectionUpdated(
        updatedMeal,
        section,
        itemId,
        text,
      );
      if (appendedNewItemId) {
        const appendIndex = fullMeal.recipes[0][section].length;
        updatedMeal = mealWithNewArraySectionItem(
          updatedMeal,
          section,
          appendIndex,
          appendedNewItemId,
          '',
        );
      }
      await updateMeal(updatedMeal);
      if (section === 'ingredients') {
        await updateFoodBrainDerivedDataOnMeal(
          updatedMeal.id,
          updatedMeal.recipes[0],
          updatedMeal.derivedNutrition?.ingredientNutrition,
          customerFoodDataID,
          lazyGetMeal,
          updateMeal,
        );
        collectIngredientEditEventOperation(
          mealId,
          recipe.mealiqId || '',
          sectionItem.id,
          originalText,
          text,
          editMethod,
          currentHealthProGroup,
        );
        await storeMealEditEventOperation(
          mealId,
          section,
          itemId,
          originalText,
          currentHealthProGroup,
        );
      }
      tracker(userProfile, ['Edit meal'], ['numMealsEdited']);
    }
  };

  const isFullMealQueryResultStale =
    mealId && fullMeal && fullMeal.id !== mealId;

  const isLoading =
    isSharedBoardLoading ||
    isSmorgBoardLoading ||
    isFullMealLoading ||
    isFullMealQueryResultStale;

  if (!visible || (!isLoading && !fullMeal)) {
    return null;
  }

  // console.log(`MealDetail mealId=${mealId}, menuId=${menuId}`);

  const onInlineEditBlur = async (section, itemId, reason) => {
    console.log(`Blur ${section} ${itemId} ${reason}`);
    let shouldDismissEditingInline = true;
    if (
      [
        'title',
        'shortDescription',
        'chef',
        'servings',
        'prepTime',
        'cookTime',
        'mealTypes',
        'utensils',
        'recipeNotes',
      ].includes(section)
    ) {
      const contentChanged =
        currentTextRef.current !== null &&
        currentTextRef.current !== undefined &&
        currentTextRef.current !== currentRecipeRef.current[section];
      if (contentChanged) {
        const updatedMeal = mealWithSingleSectionUpdated(
          fullMeal,
          section,
          currentTextRef.current,
        );
        updateMeal(updatedMeal);
        if (section === 'servings') {
          // Nutrition per serving needs to be recomputed
          await updateFoodBrainDerivedDataOnMeal(
            updatedMeal.id,
            updatedMeal.recipes[0],
            updatedMeal.derivedNutrition?.ingredientNutrition,
            customerFoodDataID,
            lazyGetMeal,
            updateMeal,
          );
        }
      }
    } else if (section === 'ingredients') {
      if (reason !== 'smorgCancel') {
        const { ingredients } = fullMeal.recipes[0];
        const index = ingredients.findIndex((ing) => ing.id === itemId);
        console.log(
          `Reason is ${reason}, editing item at index ${index} out of ${ingredients.length}`,
        );
        const editMethod =
          reason === 'selectOption' ? 'OPTION_SELECTED' : 'TEXT_TYPED';
        const pastedRows = currentTextRef.current
          .split('\n')
          .filter((row) => !!row);
        const appendNewItem =
          reason === 'enter-key' && index === ingredients.length - 1;
        const appendedNewItemId = appendNewItem && uuid();
        if (pastedRows.length >= 2) {
          console.log(`Detected paste, ${pastedRows.length} rows`);
          handleArraySectionItemsPasted(section, itemId, pastedRows);
        } else {
          console.log({ appendedNewItemId });
          handleArraySectionItemUpdated(
            section,
            itemId,
            currentTextRef.current,
            appendedNewItemId,
            editMethod,
          );
        }
        if (appendNewItem) {
          resetSuggestions();
          currentTextRef.current = '';
          focusOnField(section, appendedNewItemId);
          shouldDismissEditingInline = false;
        }
      }
    } else if (section === 'preparations') {
      const pastedRows = currentTextRef.current
        .split('\n')
        .filter((row) => !!row);
      if (pastedRows.length >= 2) {
        console.log(`Detected paste, ${pastedRows.length} rows`);
        handleArraySectionItemsPasted(section, itemId, pastedRows);
      } else {
        handleArraySectionItemUpdated(section, itemId, currentTextRef.current);
      }
    }

    if (shouldDismissEditingInline) {
      dismissEditingInline();
    }
  };

  const onUpdateIngredientStructuredData = async (
    updatedIngredient,
    updatedIngredientNutrition,
  ) => {
    console.log('onUpdateIngredientStructuredData');
    const updatedMeal = mealWithStructuredIngredientUpdated(
      fullMeal,
      updatedIngredient,
      updatedIngredientNutrition,
    );
    updateMeal(updatedMeal);
    tracker(userProfile, ['Edit meal'], ['numMealsEdited']);
  };

  const focusOnField = (section, id) => {
    setCurrentlyEditingInline({ section, itemId: id });
    window.setTimeout(() => {
      const el = document.getElementById(`${section}-${id}`);
      if (el) {
        el.focus();
      }
    }, 100);
  };

  const onAddArraySectionItem = (section) => {
    console.log(`onAddArraySectionItem ${section}`);
    const addedIndex = (fullMeal.recipes[0][section] || []).length;
    const newId = uuid();
    const updatedMeal = mealWithNewArraySectionItem(
      fullMeal,
      section,
      addedIndex,
      newId,
      '',
    );
    updateMeal(updatedMeal);
    tracker(userProfile, ['Edit meal'], ['numMealsEdited']);
    resetSuggestions();
    focusOnField(section, newId);
  };

  const onRemoveArraySectionItem = async (section, itemId) => {
    console.log(`onRemoveArraySectionItem ${section} ${itemId}`);
    const updatedMeal = mealWithArraySectionDeleted(fullMeal, section, itemId);
    updateMeal(updatedMeal);
    tracker(userProfile, ['Edit meal'], ['numMealsEdited']);
    if (section === 'ingredients') {
      await updateFoodBrainDerivedDataOnMeal(
        updatedMeal.id,
        updatedMeal.recipes[0],
        updatedMeal.derivedNutrition?.ingredientNutrition,
        customerFoodDataID,
        lazyGetMeal,
        updateMeal,
      );
    }
  };

  const onIngredientCheckChange = (ingredientId, checkState) => {
    console.log(`onIngredientCheckChange ${ingredientId} ${checkState}`);
    if (isReadOnly) {
      // dispatch(
      //   sharedMealIngredientCheckStateChangeAction(
      //     mealId,
      //     ingredientId,
      //     checkState,
      //   ),
      // );
    } else {
      const updatedMeal = mealWithIngredientCheckStateChanged(
        fullMeal,
        ingredientId,
        checkState,
      );
      updateMeal(updatedMeal);
      // TODO track
    }
  };

  const loadSuggestions = async (ingredient) => {
    setIngredientSuggestions([]);
    setIngredientSuggestionsNetworkState({ loading: true });
    try {
      await Promise.all([
        loadSuggestionsForUndo(mealId, ingredient.id),
        loadSuggestionsFromHistory(ingredient),
        loadSuggestionsFromFoodBrain(ingredient),
      ]);
      setIngredientSuggestionsNetworkState({
        loading: false,
        loaded: true,
        error: null,
      });
    } catch (e) {
      setIngredientSuggestionsNetworkState({
        loading: false,
        loaded: false,
        error: e.toString(),
      });
    }
  };

  const loadSuggestionsForUndo = async (mealID, ingredientID) => {
    const editEvents = await getEditEventsForUndoIngredientEditOperation(
      mealID,
      ingredientID,
    );
    const undoSuggestions = editEvents.map((editEvent) => ({
      suggestionText: editEvent.oldValue,
      suggestionType: 'undo',
      highlightTokens: [],
    }));
    setIngredientSuggestions((prevIngredientSuggestions) => [
      ...prevIngredientSuggestions,
      ...undoSuggestions,
    ]);
  };

  const loadSuggestionsFromHistory = async (ingredient) => {
    const ingredientEditSuggestions = await getSuggestionsFromHistoryOperation(
      ingredient.id,
      // ingredient.fullText,
    );
    const historySuggestions = ingredientEditSuggestions.map(
      (ingredientEditSuggestion) => ({
        suggestionText: ingredientEditSuggestion.suggestion,
        suggestionType: 'history',
        highlightTokens: [],
      }),
    );
    setIngredientSuggestions((prevIngredientSuggestions) => [
      ...prevIngredientSuggestions,
      ...historySuggestions,
    ]);
  };

  const loadSuggestionsFromFoodBrain = async (ingredient) => {
    const suggestionsResponse = await getSuggestionsOperation(
      [ingredient],
      locale,
      customerFoodDataID,
    );
    setIngredientSuggestions((prevIngredientSuggestions) => [
      ...prevIngredientSuggestions,
      ...suggestionsResponse[0].suggestions,
    ]);
  };

  const resetSuggestions = () => {
    setIngredientSuggestions([]);
    setIngredientSuggestionsNetworkState({
      loading: false,
      loaded: false,
      error: null,
    });
  };

  const onInlineEditFocus = (section, itemId) => {
    console.log(`Focus ${section} ${itemId}`);
    const value =
      itemId !== null
        ? currentRecipeRef.current[section].find((it) => it.id === itemId)
            .fullText
        : currentRecipeRef.current[section] || '';
    currentTextRef.current = ['mealTypes', 'utensils'].includes(section)
      ? value
      : value.toString();
    if (section === 'ingredients') {
      const ingredient = currentRecipeRef.current[section].find(
        (it) => it.id === itemId,
      );
      loadSuggestions(ingredient);
    }
    setCurrentlyEditingInline({ section, itemId });
  };

  const onInlineEditChange = (section, itemId, value) => {
    console.log(`Change ${section} ${itemId}`);
    currentTextRef.current = value;
  };

  const onDialogClose = (ev, reason) => {
    if (reason === 'escapeKeyDown') {
      console.log('Dialog escape key down');
      // Prevent dialog from closing, let the contenteditable handle the esc key
      return null;
    }
    console.log(`Dialog close other reason: ${reason}`);
    dismiss();
    return true;
  };

  const onNewImageUrlSet = async (newImageUrl) => {
    console.log(`onNewImageUrlSet ${newImageUrl}`);
    const updatedMeal = {
      ...fullMeal,
      recipes: [
        {
          ...fullMeal.recipes[0],
          imageUrl: newImageUrl,
        },
      ],
    };
    updateMeal(updatedMeal);
  };

  const onNewImageChosen = async (fileList) => {
    console.log(`onNewImageChosen`);
    const newImageUrl = await uploadUserImage(mealId, fileList);
    const updatedMeal = {
      ...fullMeal,
      recipes: [
        {
          ...fullMeal.recipes[0],
          imageUrl: newImageUrl,
        },
      ],
    };
    updateMeal(updatedMeal);
  };

  const onRuleChanged = async (itemId, rule) => {
    console.log(`onRuleChanged ${itemId} ${JSON.stringify(rule)}`);
    const updatedMeal = mealWithIngredientScalingRuleUpdated(
      fullMeal,
      itemId,
      rule,
    );
    updateMeal(updatedMeal);
  };

  const canScaleIngredients =
    recipe &&
    recipe.ingredients.some(
      (ing) => !!ing.tokens && !!ing.structuredIngredient,
    );

  const onEditScaleToServings = (newScaleToServings) => {
    setScaleToServings(newScaleToServings);
  };

  const onChangeScaleToServings = async () => {
    console.log(`onChangeScaleToServings ${scaleToServings}`);
    const scaleToServingsNumber = Number(scaleToServings) || 1;
    if (scaleToServingsNumber !== recipe.servings) {
      const scaleFactor = scaleToServingsNumber / (recipe.servings || 1);
      setSavingInProgress(true);
      try {
        // if (isSharedProgrammeBoard) {
        //   await dispatch(
        //     locallyScaleSharedMealAction(
        //       mealId,
        //       scaleFactor,
        //       scaleToServingsNumber,
        //     ),
        //   );
        // } else {
        await scaleMeal(
          fullMeal,
          scaleFactor,
          scaleToServingsNumber,
          customerFoodDataID,
          false,
          updateMeal,
          dispatch,
          lazyGetMeal,
        );
        // }
      } catch (e) {
        setScaleToServings(recipe.servings);
        throw e;
      } finally {
        setSavingInProgress(false);
      }
    }
  };

  const resetScaleToServings = () => {
    setScaleToServings(recipe.servings);
  };

  const showNutritionMetrics = [
    'calories',
    'fat',
    'saturates',
    'carbohydrate',
    'protein',
    'fibre',
    'sugar',
  ];

  const onTagsChange = (tags) => {
    console.log(`onTagsChange ${JSON.stringify(tags)}`);
    const nonBlankTags = tags.filter(
      (t) => !!t && JSON.stringify(t).length > 0,
    );
    const currentRecipeTags = recipe.tags || [];
    if (JSON.stringify(currentRecipeTags) !== JSON.stringify(nonBlankTags)) {
      const updatedMeal = {
        ...fullMeal,
        recipes: [
          {
            ...fullMeal.recipes[0],
            tags: nonBlankTags,
          },
        ],
      };
      updateMeal(updatedMeal);
      const changedTags = addedItems(nonBlankTags, currentRecipeTags);
      if (changedTags.length > 0) {
        const MAX_RECENTLY_USED_TAGS = 5;
        const oldRecentlyUsedTags = userProfile?.recentlyUsedTags || [];
        const newRecentlyUsedTags = deduplicate([
          ...changedTags,
          ...oldRecentlyUsedTags,
        ]).slice(0, MAX_RECENTLY_USED_TAGS);
        updateUserProfile({
          ...userProfile,
          recentlyUsedTags: newRecentlyUsedTags,
        });
      }
    }
  };

  const alwaysShowNutrition = !isCommunityUser;

  return (
    <MealDetailModal
      fullMeal={fullMeal}
      isLoading={isLoading}
      menu={menu}
      isReadOnly={isReadOnly}
      savingInProgress={savingInProgress}
      alwaysShowNutrition={alwaysShowNutrition}
      showNutritionMetrics={showNutritionMetrics}
      derivedNutrition={fullMeal?.derivedNutrition}
      canScaleIngredients={canScaleIngredients}
      scaleToServings={scaleToServings}
      currentlyEditingInline={currentlyEditingInline}
      ingredientSuggestions={ingredientSuggestions}
      ingredientSuggestionsNetworkState={ingredientSuggestionsNetworkState}
      onInlineEditFocus={onInlineEditFocus}
      onInlineEditChange={onInlineEditChange}
      onInlineEditBlur={onInlineEditBlur}
      onAddArraySectionItem={onAddArraySectionItem}
      onRemoveArraySectionItem={onRemoveArraySectionItem}
      onIngredientCheckChange={onIngredientCheckChange}
      onEditScaleToServings={onEditScaleToServings}
      onChangeScaleToServings={onChangeScaleToServings}
      resetScaleToServings={resetScaleToServings}
      onNewImageUrlSet={onNewImageUrlSet}
      onNewImageChosen={onNewImageChosen}
      onRuleChanged={onRuleChanged}
      onAddonChange={async (addonName, enabled) => {
        console.log(`onAddonChange ${addonName} ${enabled}`);
        if (addonName === SMORG_NUTRITION_ADDON && enabled) {
          tracker(
            userProfile,
            ['Nutrition added to meal'],
            ['numNutritionClicks'],
          );
        }
        const updatedMeal = mealWithAddonEnabled(fullMeal, addonName, enabled);
        await updateMeal(updatedMeal);
      }}
      onTagsChange={onTagsChange}
      onDialogClose={onDialogClose}
      onUpdateIngredientStructuredData={onUpdateIngredientStructuredData}
    />
  );
};

MealDetailContainer.propTypes = {
  parentSmorgBoardId: PropTypes.string.isRequired,
  mealId: PropTypes.string,
  menuId: PropTypes.string,
  visible: PropTypes.bool.isRequired,
  // isPreview: PropTypes.bool,
  dismiss: PropTypes.func,
};

MealDetailContainer.defaultProps = {
  mealId: null,
  menuId: null,
  // isPreview: false,
  dismiss: () => {},
};

export default MealDetailContainer;
